import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import {
  deleteOperator,
  machineOperatorsQueryOptions,
  updateOperator,
} from '@/api/resources/machine-operators'
import { EditOperatorDialog } from '@/components/machines/operators/edit-operator-dialog'
import { useRouteModal } from '@/hooks/use-route-modal'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/machines/$machineCode/operators_/$userId/edit')({
  head: () => ({ meta: [{ title: 'Edit Operator - MyJunea' }] }),
  component: EditOperatorRoute,
})

function EditOperatorRoute(): React.ReactElement {
  const { machineCode, userId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const numericUserId = Number(userId)
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({ to: '/machines/$machineCode', params: { machineCode }, resetScroll: false }),
  )

  const { data: operators } = useQuery(machineOperatorsQueryOptions(machineCode))
  const operator = operators?.find((o) => o.id === numericUserId)

  const [emailOptin, setEmailOptin] = useState<boolean | null>(null)
  const currentOptin = emailOptin ?? operator?.email_optin ?? false

  const updateMutation = useMutation({
    mutationFn: async () =>
      updateOperator(machineCode, numericUserId, { email_optin: currentOptin }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.operators(machineCode) })
      toast.success('Operator updated successfully')
      handleClose()
    },
    onError: (error: Error) => {
      const message = error instanceof ApiError ? error.body.message : 'Failed to update operator'
      toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => deleteOperator(machineCode, numericUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.operators(machineCode) })
      toast.success('Operator removed successfully')
      handleClose()
    },
    onError: (error: Error) => {
      const message = error instanceof ApiError ? error.body.message : 'Failed to remove operator'
      toast.error(message)
    },
  })

  const operatorName = operator ? `${operator.first_name} ${operator.last_name}` : 'this operator'

  return (
    <EditOperatorDialog
      open={open}
      onOpenChange={handleOpenChange}
      operatorName={operatorName}
      emailOptin={currentOptin}
      onEmailOptinChange={setEmailOptin}
      onSave={() => updateMutation.mutate()}
      onDelete={() => deleteMutation.mutate()}
      isSaving={updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
    />
  )
}
